let initialized = false;

/**
 * GA4 recommended event names for consistent reporting.
 * https://support.google.com/analytics/answer/9267735
 */
export const AnalyticsEvents = {
  SIGN_UP: 'sign_up',
  LOGIN: 'login',
  LOGOUT: 'logout',
  PURCHASE: 'purchase',
  SUBSCRIPTION_CANCELED: 'subscription_canceled',
  ONBOARDING_STEP: 'onboarding_step',
  ONBOARDING_COMPLETED: 'onboarding_completed',
  PASSWORD_RESET_REQUESTED: 'password_reset_requested',
  CAPTURE_PAGE_VIEW: 'capture_page_view',
  CAPTURE_PAGE_CONVERSION: 'capture_page_conversion',
  SEO_CTA_CLICK: 'seo_cta_click',
  ONBOARDING_GOAL_SELECTED: 'onboarding_goal_selected',
  ONBOARDING_PREVIEW_CONTINUED: 'onboarding_preview_continued',
  PLAN_GENERATION_FAILED: 'plan_generation_failed',
  ONBOARDING_STEP_VIEWED: 'onboarding_step_viewed',
  FIRST_TRACKING_LOGGED: 'first_tracking_logged',
  FIRST_AI_INSIGHT_GENERATED: 'first_ai_insight_generated',
  GETTING_STARTED_ITEM_COMPLETED: 'getting_started_item_completed',
  GETTING_STARTED_DISMISSED: 'getting_started_dismissed',
  FEATURE_UPSELL_VIEWED: 'feature_upsell_viewed',
  FEATURE_UPSELL_ACCEPTED: 'feature_upsell_accepted',
  EMAIL_LINK_CLICKED: 'email_link_clicked',
};

/**
 * Which kind of logging a tracking field represents. Reporting on the raw field
 * name would split "logged some food" across five rows.
 */
const TRACKING_FIELD_TYPES = {
  water: 'water',
  weight: 'weight',
  caloriesConsumed: 'food',
  proteins: 'food',
  carbs: 'food',
  fats: 'food',
  saturatedFat: 'food',
  caloriesBurned: 'exercise',
  exerciseDuration: 'exercise',
  steps: 'exercise',
};

export function initGA() {
  const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID;
  const googleAdsId = import.meta.env.VITE_GOOGLE_ADS_ID;
  if ((!measurementId && !googleAdsId) || initialized || isLocal) return;

  const tagId = measurementId || googleAdsId;
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${tagId}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function () {
    window.dataLayer.push(arguments);
  };
  window.gtag('js', new Date());

  if (measurementId) {
    window.gtag('config', measurementId, {
      send_page_view: false,
    });
  }

  if (googleAdsId) {
    window.gtag('config', googleAdsId);
  }

  initialized = true;
}

export function trackPageView(path, title) {
  if (!window.gtag) return;

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title,
  });
}

export function trackEvent(eventName, params = {}) {
  if (!window.gtag) return;

  window.gtag('event', eventName, params);
}

export function trackEventOnce(eventName, params = {}) {
  if (!window.gtag) return;

  const key = `as_tracked_${eventName}`;
  if (localStorage.getItem(key)) return;

  window.gtag('event', eventName, params);
  localStorage.setItem(key, Date.now().toString());
}

/**
 * The first value an account ever logs, wherever it was logged from.
 *
 * This belongs to the mutation rather than to the components that call it.
 * Water, weight, food and exercise are written from six different surfaces and
 * the event had only ever been wired into the two AI ones — both paid — so a
 * free user could log water every day and never register as activated at all.
 */
export function trackFirstTrackingLogged(field) {
  trackEventOnce(AnalyticsEvents.FIRST_TRACKING_LOGGED, { type: TRACKING_FIELD_TYPES[field] || field });
}

/**
 * Google Ads deduplicates repeated conversions by `transaction_id`, so the value
 * only has to be stable and unique per conversion — the user id is the natural
 * choice for a sign-up.
 *
 * It must never be a credential. The value is pushed onto `window.dataLayer`,
 * sent to Google and stored against the conversion in the Ads account, so
 * anything with access to the page or to the account can read it.
 */
export function trackGoogleAdsConversion(conversionId) {
  if (!window.gtag) return;

  const googleAdsId = import.meta.env.VITE_GOOGLE_ADS_ID;
  const conversionLabel = import.meta.env.VITE_GOOGLE_ADS_CONVERSION_LABEL;

  if (!googleAdsId || !conversionLabel) return;

  window.gtag('event', 'conversion', {
    send_to: `${googleAdsId}/${conversionLabel}`,
    transaction_id: conversionId,
  });
}
